import { useSetAtom } from 'jotai';
import React, { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { updateMemberNotificationsMark } from '@/api/notification/updateNotification';
import { deleteNotification } from '@/api/public';
import { EmptyBellIcon, XMarkIcon, ZigtruckCircleColorIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { ACTIVITY_NOTIFICATION_TYPES } from '@/const/notification';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { isExistNotReadNotificationAtom } from '@/store/notification';
import { calculateTimeAgo, isExternalUrl, isUrlFromOurDomain } from '@/utils/common';
import { Avatar, Skeleton } from '@mui/material';
import Box from '@mui/material/Box';

interface InterestNotificationProps {
  isEditMode: boolean;
  data: Notifications[] | undefined;
  notReadNotificationCount: number;
  isLoading: boolean;
}

const ActivityNotification = ({ isEditMode, data, notReadNotificationCount, isLoading }: InterestNotificationProps) => {
  const { showToast } = useToastContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setIsExistNotReadNotification = useSetAtom(isExistNotReadNotificationAtom);

  const notReadCountRef = useRef(notReadNotificationCount);

  useEffect(() => {
    notReadCountRef.current = notReadNotificationCount;
  }, [notReadNotificationCount]);

  const onClickDelete = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    mutate(id);
  };

  const { mutate, error, isSuccess } = useMutation(deleteNotification, {
    onSuccess: (deletedId) => {
      const currentData = queryClient.getQueryData<NotificationsResponse>(['get-notification']);
      if (!currentData) return;

      const prevData = { ...currentData };
      const prevNotification = prevData.activityNotifications;
      const prevCount = prevData.notReadActivityNotificationCount;

      const notificationToDelete = prevNotification?.find((item) => item.id === deletedId);

      const isUnread = notificationToDelete && !notificationToDelete.isRead;

      queryClient.setQueryData(['get-notification'], {
        ...prevData,
        activityNotifications: prevNotification?.filter((item) => item.id !== deletedId),
        notReadActivityNotificationCount: isUnread ? prevCount - 1 : prevCount,
      });
    },
    onError: () => {
      showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
    },
  });

  const updateMemberNotificationsMarkMutation = useMutation((type: string) => updateMemberNotificationsMark(type), {
    onMutate: async () => {
      await queryClient.cancelQueries('get-notification');

      const currentData = queryClient.getQueryData<NotificationsResponse>(['get-notification']);
      if (!currentData) return;

      const prevData = { ...currentData };

      const newActivityNotifications = prevData.activityNotifications?.map((item) => ({
        ...item,
        isRead: true,
      }));

      queryClient.setQueryData(['get-notification'], {
        ...prevData,
        activityNotifications: newActivityNotifications,
        notReadActivityNotificationCount: 0,
      });

      return { prevData };
    },
    onSuccess: () => {
      const updateData = queryClient.getQueryData<NotificationsResponse>(['get-notification']);
      if (
        updateData?.notReadActivityNotificationCount === 0 &&
        updateData?.notReadInterestProductNotificationCount === 0
      ) {
        setIsExistNotReadNotification(false);
      }
    },
    onError: (error, data, context) => {
      queryClient.setQueryData(['get-notification'], { ...context?.prevData });
      showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
    },
  });

  const onClickRedirect = (redirectUrl: string) => {
    if (isExternalUrl(redirectUrl)) {
      if (isUrlFromOurDomain(redirectUrl)) {
        const url = new URL(redirectUrl);
        if (!!url.hash) {
          navigate(url.pathname + url.hash);
        } else {
          navigate(url.pathname);
        }
      } else {
        window.open(redirectUrl);
      }
    } else {
      navigate(redirectUrl);
    }
  };

  const onClickNotification = (memberNotificationsId: number, redirectUrl: string) => {
    onClickRedirect(redirectUrl);
  };

  const skeletonCount = Math.ceil(window.innerHeight / 100);

  useEffect(() => {
    return () => {
      if (notReadCountRef.current > 0) {
        updateMemberNotificationsMarkMutation.mutate(ACTIVITY_NOTIFICATION_TYPES);
      }
    };
  }, []);

  return (
    <>
      <div className="w-full min-w-[320px] max-w-[720px] pt-[48px]">
        {isLoading ? (
          <div>
            {Array.from(new Array(skeletonCount)).map((_, index) => (
              <Box sx={{ display: 'flex', alignItems: 'center', paddingX: '16px', height: '96px' }} key={index}>
                <Skeleton variant="circular" width={52} height={52}>
                  <Avatar />
                </Skeleton>
                <div className="w-full ml-[9px] flex-1">
                  <Skeleton width="100%" variant="text" sx={{ fontSize: '1rem' }} />
                  <Skeleton width="60%" variant="text" sx={{ fontSize: '1rem' }} />
                  <Skeleton width="10%" variant="text" sx={{ fontSize: '1rem' }} />
                </div>
              </Box>
            ))}
          </div>
        ) : (
          <div className="flex flex-col justify-between w-full h-full">
            {data?.length === 0 ? (
              <div className="w-full h-full px-4" style={{ height: `calc(100vh - 180px)` }}>
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <EmptyBellIcon color={colors.gray[4]} width={120} height={120}></EmptyBellIcon>
                  <span className="mt-4 text-lg text-gray-7">받은 알림이 없어요.</span>
                </div>
              </div>
            ) : (
              <ul className="min-w-[248px] pb-24">
                {data?.map((item, index) => {
                  const notification = item?.notification;
                  return (
                    <li
                      key={item.id}
                      className={`relative flex item-center px-2 py-4 cursor-pointer ${item.isRead ? '' : 'bg-blue-1'}`}
                      onClick={() => onClickNotification(item.id, notification.redirectUrl)}
                    >
                      <div className="rounded-full w-[52px] h-[52px] min-w-[52px] mx-2 my-1">
                        {notification.imageUrl ? (
                          <img
                            src={notification.imageUrl}
                            alt="profile-img"
                            className="object-cover w-full h-full rounded-full"
                          />
                        ) : (
                          <span className="w-full h-full">
                            <ZigtruckCircleColorIcon width={52} height={52}></ZigtruckCircleColorIcon>
                          </span>
                        )}
                      </div>
                      <dl className="flex-1 mr-6">
                        <dt className="flex items-center mb-1 leading-5">
                          <span className="py-1 font-semibold text-gray-8">{notification.title}</span>
                        </dt>
                        <dd
                          className={`flex-1 max-w-[500px] font-medium text-gray-7 text-xs line-clamp-2 text-ellipsis break-all mb-1`}
                        >
                          {notification.contents}
                        </dd>
                        <dd className="text-xs text-gray-5">{calculateTimeAgo(notification.sentDate)}</dd>
                        {isEditMode && (
                          <div
                            className="absolute right-0 m-4 cursor-pointer top-2"
                            onClick={(e) => onClickDelete(e, item.id)}
                          >
                            <XMarkIcon color={colors.gray[9]}></XMarkIcon>
                          </div>
                        )}
                      </dl>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </>
  );
};
export default ActivityNotification;
